/**
 * Budget planning against a chosen WBS hierarchy.
 *
 * Budget Management cost-plans against the Cost Management WBS by default, but a
 * project can plan against its Execution WBS or Financial WBS instead. Budget
 * values live in `baseline_wbs_summaries`, one row per WBS element per baseline,
 * with a `cost_by_category` breakdown. This module is the pure half:
 *
 *  - composeBudgetSummaries: lay the stored rows over the chosen hierarchy's
 *    nodes (match by element id, then code) and roll the values up the tree.
 *  - applyBudgetEdits: apply staged category edits to leaf elements and roll up
 *    again, so the screen shows the result before anything is saved.
 *  - changedBudgetRows / bcrAffectedFromEdits: what a save writes, and the
 *    before / after values a change record carries.
 *  - bucketPeriod: map a stored period label onto a month, quarter or year.
 *
 * Rollup rule: an element with children shows the sum of its children. If none
 * of its descendants carries any value, its own stored value is kept, so budget
 * planned at a summary level is not wiped by an empty subtree.
 */
import type { BaselineChangeRequest, BaselineWbsSummary, BcrAffectedElement, BcrStatus } from '@/types/ppm'

export type BudgetWbsSource = 'cost' | 'scheduling' | 'financial'

export const BUDGET_WBS_SOURCES: readonly BudgetWbsSource[] = ['cost', 'scheduling', 'financial']

export const BUDGET_WBS_SOURCE_LABEL: Record<BudgetWbsSource, string> = {
  cost: 'Cost Management WBS',
  scheduling: 'Execution WBS',
  financial: 'Financial WBS',
}

export function isBudgetWbsSource(v: unknown): v is BudgetWbsSource {
  return typeof v === 'string' && (BUDGET_WBS_SOURCES as readonly string[]).includes(v)
}

/** A WBS element of whichever hierarchy the budget is planned against. */
export interface PlanNode {
  id: string
  parentId: string | null
  code: string
  name: string
  level: number
  sortOrder: number
  type: string
}

/** A stored summary row, plus the row id the save needs. */
export type PlanSummary = BaselineWbsSummary & { id?: string }

export type CategoryLine = BaselineWbsSummary['costByCategory'][number]

/** Staged edits: element id -> category id -> new total cost. */
export type BudgetEdits = Record<string, Record<string, { total: number; name: string }>>

const num = (v: unknown): number => {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

const round2 = (v: number) => Math.round(v * 100) / 100

/**
 * Id of the line that carries the part of an element's stored total that its
 * category lines do not explain (rows loaded with totals only). It is shown and
 * editable like a category, so editing a real category never drops that money,
 * and it is stripped again before a save (see changedBudgetRows).
 */
export const UNCATEGORIZED_ID = '__uncategorized'
export const UNCATEGORIZED_NAME = 'Not assigned to a category'

/** Postgres numerics arrive as strings; make every amount a number. */
export function normalizeSummary(s: PlanSummary): PlanSummary {
  const n = normalizeAmounts(s)
  const cats = n.costByCategory.filter((c) => c.resourceCategoryId !== UNCATEGORIZED_ID)
  const base = round2(n.totalBaseCost - cats.reduce((t, c) => t + c.baseCost, 0))
  const burden = round2(n.totalBurdenCost - cats.reduce((t, c) => t + c.burdenCost, 0))
  const total = round2(n.totalCost - cats.reduce((t, c) => t + c.totalCost, 0))
  if (Math.abs(total) < 0.5 && Math.abs(base) < 0.5 && Math.abs(burden) < 0.5) return { ...n, costByCategory: cats }
  return {
    ...n,
    costByCategory: [...cats, {
      resourceCategoryId: UNCATEGORIZED_ID,
      resourceCategoryName: UNCATEGORIZED_NAME,
      baseCost: base,
      burdenCost: burden,
      totalCost: total,
      hours: null,
    }],
  }
}

function normalizeAmounts(s: PlanSummary): PlanSummary {
  return {
    ...s,
    level: num(s.level),
    totalBaseCost: num(s.totalBaseCost),
    totalBurdenCost: num(s.totalBurdenCost),
    totalCost: num(s.totalCost),
    totalBaseRevenue: num(s.totalBaseRevenue),
    totalBurdenRevenue: num(s.totalBurdenRevenue),
    totalRevenue: num(s.totalRevenue),
    costByCategory: (Array.isArray(s.costByCategory) ? s.costByCategory : []).map((c) => ({
      ...c,
      baseCost: num(c.baseCost),
      burdenCost: num(c.burdenCost),
      totalCost: num(c.totalCost),
      hours: c.hours == null ? null : num(c.hours),
    })),
  }
}

function hasValue(s: Pick<BaselineWbsSummary, 'totalCost' | 'totalRevenue' | 'costByCategory'>): boolean {
  return s.totalCost !== 0 || s.totalRevenue !== 0 || s.costByCategory.some((c) => c.totalCost !== 0)
}

function sumCategories(lists: CategoryLine[][]): CategoryLine[] {
  const acc = new Map<string, CategoryLine>()
  for (const list of lists) {
    for (const c of list) {
      const ex = acc.get(c.resourceCategoryId)
      if (ex) {
        ex.baseCost += c.baseCost
        ex.burdenCost += c.burdenCost
        ex.totalCost += c.totalCost
        ex.hours = ex.hours != null || c.hours != null ? (ex.hours ?? 0) + (c.hours ?? 0) : null
      } else {
        acc.set(c.resourceCategoryId, { ...c })
      }
    }
  }
  return Array.from(acc.values())
}

/** Totals of a node from its category lines; revenue is carried as stored. */
function withCategoryTotals(s: PlanSummary, cats: CategoryLine[]): PlanSummary {
  return {
    ...s,
    costByCategory: cats,
    totalBaseCost: round2(cats.reduce((t, c) => t + c.baseCost, 0)),
    totalBurdenCost: round2(cats.reduce((t, c) => t + c.burdenCost, 0)),
    totalCost: round2(cats.reduce((t, c) => t + c.totalCost, 0)),
  }
}

/**
 * Roll values up a summary list whose parent links are already set. Children
 * are summed into parents (cost, revenue and categories); a parent whose whole
 * subtree is empty keeps its own value.
 */
export function rollUp(summaries: PlanSummary[]): PlanSummary[] {
  const byId = new Map(summaries.map((s) => [s.wbsElementId, { ...s }]))
  const children = new Map<string, string[]>()
  for (const s of summaries) {
    if (s.parentWbsElementId && byId.has(s.parentWbsElementId)) {
      const list = children.get(s.parentWbsElementId) ?? []
      list.push(s.wbsElementId)
      children.set(s.parentWbsElementId, list)
    }
  }
  const done = new Set<string>()
  const visit = (id: string, guard: Set<string>): PlanSummary => {
    const node = byId.get(id)!
    if (done.has(id) || guard.has(id)) return node
    guard.add(id)
    const kids = (children.get(id) ?? []).map((k) => visit(k, guard))
    if (kids.length > 0 && kids.some(hasValue)) {
      const rolled = withCategoryTotals(node, sumCategories(kids.map((k) => k.costByCategory)))
      // Categories can be absent on rows that only carry totals; fall back to
      // summing the children's totals so nothing is lost.
      if (rolled.costByCategory.length === 0) {
        rolled.totalBaseCost = round2(kids.reduce((t, k) => t + k.totalBaseCost, 0))
        rolled.totalBurdenCost = round2(kids.reduce((t, k) => t + k.totalBurdenCost, 0))
        rolled.totalCost = round2(kids.reduce((t, k) => t + k.totalCost, 0))
      }
      rolled.totalBaseRevenue = round2(kids.reduce((t, k) => t + k.totalBaseRevenue, 0))
      rolled.totalBurdenRevenue = round2(kids.reduce((t, k) => t + k.totalBurdenRevenue, 0))
      rolled.totalRevenue = round2(kids.reduce((t, k) => t + k.totalRevenue, 0))
      byId.set(id, rolled)
    }
    done.add(id)
    return byId.get(id)!
  }
  for (const s of summaries) visit(s.wbsElementId, new Set())
  return summaries.map((s) => byId.get(s.wbsElementId)!)
}

export interface ComposedBudget {
  summaries: PlanSummary[]
  /** Stored rows that match no element of the chosen hierarchy. */
  unmatched: number
}

/**
 * Lay the stored rows over the chosen hierarchy. With no nodes (the hierarchy
 * is empty or not loaded) the stored rows are shown as they are.
 */
export function composeBudgetSummaries(
  nodes: PlanNode[],
  stored: PlanSummary[],
  baselineModelId: string,
): ComposedBudget {
  const rows = stored.map(normalizeSummary)
  if (nodes.length === 0) {
    // A row whose parent has no row of its own (e.g. the project root was never
    // stored) is shown at the top level instead of disappearing from the tree.
    const ids = new Set(rows.map((r) => r.wbsElementId))
    const rooted = rows.map((r) =>
      r.parentWbsElementId && !ids.has(r.parentWbsElementId) ? { ...r, parentWbsElementId: null } : r,
    )
    return { summaries: rollUp(rooted), unmatched: 0 }
  }

  const byId = new Map(rows.map((r) => [r.wbsElementId, r]))
  const byCode = new Map<string, PlanSummary>()
  for (const r of rows) if (r.wbsElementCode && !byCode.has(r.wbsElementCode)) byCode.set(r.wbsElementCode, r)
  const nodeIds = new Set(nodes.map((n) => n.id))
  const used = new Set<PlanSummary>()

  const ordered = [...nodes].sort((a, b) => a.level - b.level || a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
  const composed: PlanSummary[] = ordered.map((n) => {
    const match = byId.get(n.id) ?? (n.code ? byCode.get(n.code) : undefined)
    if (match) used.add(match)
    const base: PlanSummary = match ?? {
      baselineModelId,
      wbsElementId: n.id,
      wbsElementCode: n.code,
      wbsElementName: n.name,
      wbsType: n.type,
      parentWbsElementId: null,
      level: n.level,
      burdenRateDeckOverrideId: null,
      burdenRateDeckOverrideName: null,
      totalBaseCost: 0,
      totalBurdenCost: 0,
      totalCost: 0,
      totalBaseRevenue: 0,
      totalBurdenRevenue: 0,
      totalRevenue: 0,
      costByCategory: [],
    }
    return {
      ...base,
      // Structure always comes from the hierarchy; values from the stored row.
      wbsElementId: n.id,
      wbsElementCode: n.code,
      wbsElementName: n.name,
      wbsType: n.type,
      parentWbsElementId: n.parentId && nodeIds.has(n.parentId) ? n.parentId : null,
      level: n.level,
    }
  })
  return { summaries: rollUp(composed), unmatched: rows.filter((r) => !used.has(r)).length }
}

/** Ids of elements with children, from a composed list. */
export function parentIds(summaries: PlanSummary[]): Set<string> {
  return new Set(summaries.map((s) => s.parentWbsElementId).filter((p): p is string => !!p))
}

/** Burden as a share of total cost across the composed leaves; 0 when unknown. */
export function prevailingBurdenShare(summaries: PlanSummary[]): number {
  const parents = parentIds(summaries)
  let burden = 0
  let total = 0
  for (const s of summaries) {
    if (parents.has(s.wbsElementId)) continue
    for (const c of s.costByCategory) { burden += c.burdenCost; total += c.totalCost }
  }
  const share = total !== 0 ? burden / total : 0
  return share > 0 && share < 1 ? share : 0
}

/** One category line with a new total, keeping its base / burden split. */
export function rebaseCategory(line: CategoryLine | undefined, id: string, name: string, total: number, fallbackBurdenShare: number): CategoryLine {
  const exTotal = line ? line.baseCost + line.burdenCost : 0
  if (line && exTotal !== 0) {
    const ratio = total / exTotal
    return {
      ...line,
      baseCost: round2(line.baseCost * ratio),
      burdenCost: round2(line.burdenCost * ratio),
      totalCost: round2(total),
      hours: line.hours != null ? round2(line.hours * ratio) : null,
    }
  }
  return {
    resourceCategoryId: id,
    resourceCategoryName: line?.resourceCategoryName ?? name,
    baseCost: round2(total * (1 - fallbackBurdenShare)),
    burdenCost: round2(total * fallbackBurdenShare),
    totalCost: round2(total),
    hours: line?.hours ?? null,
  }
}

/** Apply staged edits to leaf elements, then roll up. */
export function applyBudgetEdits(summaries: PlanSummary[], edits: BudgetEdits): PlanSummary[] {
  const ids = Object.keys(edits)
  if (ids.length === 0) return summaries
  const parents = parentIds(summaries)
  const share = prevailingBurdenShare(summaries)
  const next = summaries.map((s) => {
    const e = edits[s.wbsElementId]
    if (!e || parents.has(s.wbsElementId)) return s
    const lines = new Map(s.costByCategory.map((c) => [c.resourceCategoryId, c]))
    for (const [catId, v] of Object.entries(e)) {
      lines.set(catId, rebaseCategory(lines.get(catId), catId, v.name, v.total, share))
    }
    const cats = Array.from(lines.values()).filter((c) => c.totalCost !== 0 || (c.hours ?? 0) !== 0)
    return withCategoryTotals(s, cats)
  })
  return rollUp(next)
}

/** Element ids whose stored row must be written: edited leaves and their ancestors. */
export function affectedIds(summaries: PlanSummary[], edits: BudgetEdits): Set<string> {
  const parentOf = new Map(summaries.map((s) => [s.wbsElementId, s.parentWbsElementId]))
  const out = new Set<string>()
  for (const id of Object.keys(edits)) {
    let cur: string | null | undefined = id
    const guard = new Set<string>()
    while (cur && !guard.has(cur)) {
      guard.add(cur)
      out.add(cur)
      cur = parentOf.get(cur) ?? null
    }
  }
  return out
}

/**
 * Rows a save writes, in the composed shape (carrying the stored row id when
 * there is one). The uncategorized line stays in the totals but is not stored
 * as a category.
 */
export function changedBudgetRows(after: PlanSummary[], edits: BudgetEdits): PlanSummary[] {
  const ids = affectedIds(after, edits)
  return after
    .filter((s) => ids.has(s.wbsElementId))
    .map((s) => ({ ...s, costByCategory: s.costByCategory.filter((c) => c.resourceCategoryId !== UNCATEGORIZED_ID) }))
}

/** Before / after budget of each edited element, for the change record. */
export function bcrAffectedFromEdits(before: PlanSummary[], after: PlanSummary[], edits: BudgetEdits): BcrAffectedElement[] {
  const b = new Map(before.map((s) => [s.wbsElementId, s]))
  return after
    .filter((s) => edits[s.wbsElementId])
    .map((s) => {
      const prev = b.get(s.wbsElementId)
      const bacBefore = prev?.totalCost ?? 0
      return {
        wbsElementId: s.wbsElementId,
        wbsElementCode: s.wbsElementCode,
        wbsElementName: s.wbsElementName,
        wbsType: s.wbsType,
        bacBefore,
        bacAfter: s.totalCost,
        budgetDelta: round2(s.totalCost - bacBefore),
        hoursDelta: null,
        newStartDate: null,
        newFinishDate: null,
      }
    })
    .filter((a) => a.budgetDelta !== 0)
}

/** Change records a budget edit can still be tied to. */
const OPEN_BCR_STATUSES: readonly BcrStatus[] = ['Draft', 'Submitted', 'Under Review', 'PendingApproval', 'Approved']

export function openBcrsForBaseline(bcrs: BaselineChangeRequest[], baselineModelId: string): BaselineChangeRequest[] {
  return bcrs.filter((b) => b.baselineModelId === baselineModelId && OPEN_BCR_STATUSES.includes(b.status))
}

/**
 * Merge new affected elements into a change record's list. An element already
 * on the record keeps its original "before" value; its "after" moves.
 */
export function mergeAffectedElements(existing: BcrAffectedElement[], incoming: BcrAffectedElement[]): BcrAffectedElement[] {
  const out = existing.map((e) => ({ ...e }))
  for (const inc of incoming) {
    const ex = out.find((e) => e.wbsElementId === inc.wbsElementId)
    if (ex) {
      ex.bacAfter = inc.bacAfter
      ex.budgetDelta = round2(inc.bacAfter - ex.bacBefore)
    } else {
      out.push(inc)
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Time phasing
// ---------------------------------------------------------------------------

export type PeriodGrain = 'Month' | 'Quarter' | 'Year'

/** Month (1-12) holding the Thursday of an ISO week. */
function isoWeekMonth(year: number, week: number): { year: number; month: number } {
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const day = jan4.getUTCDay() || 7
  const mondayW1 = new Date(jan4)
  mondayW1.setUTCDate(jan4.getUTCDate() - day + 1)
  const thursday = new Date(mondayW1)
  thursday.setUTCDate(mondayW1.getUTCDate() + (week - 1) * 7 + 3)
  return { year: thursday.getUTCFullYear(), month: thursday.getUTCMonth() + 1 }
}

/**
 * Map a stored period label ("2025-01", "2025-W04", "2025-Q1", "2025") onto
 * the requested grain. A label coarser than the grain stays as it is (a
 * quarterly entry shown monthly keeps its "2025-Q1" column). Unknown labels
 * pass through.
 */
export function bucketPeriod(period: string, grain: PeriodGrain): string {
  const p = (period ?? '').trim()
  let year: number | null = null
  let month: number | null = null
  let quarter: number | null = null
  let m: RegExpMatchArray | null
  if ((m = p.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/))) { year = +m[1]!; month = +m[2]! }
  else if ((m = p.match(/^(\d{4})-W(\d{1,2})$/i))) { const r = isoWeekMonth(+m[1]!, +m[2]!); year = r.year; month = r.month }
  else if ((m = p.match(/^(\d{4})-Q([1-4])$/i))) { year = +m[1]!; quarter = +m[2]! }
  else if ((m = p.match(/^(\d{4})$/))) { year = +m[1]! }
  else return p
  if (grain === 'Year') return String(year)
  if (month != null) quarter = Math.ceil(month / 3)
  if (grain === 'Quarter') return quarter != null ? `${year}-Q${quarter}` : String(year)
  if (month != null) return `${year}-${String(month).padStart(2, '0')}`
  return quarter != null ? `${year}-Q${quarter}` : String(year)
}

/** Sort period labels chronologically ("2025-Q1" sits before "2025-04"). */
export function comparePeriods(a: string, b: string): number {
  const key = (p: string) => {
    let m: RegExpMatchArray | null
    if ((m = p.match(/^(\d{4})-(\d{2})$/))) return +m[1]! * 100 + +m[2]!
    if ((m = p.match(/^(\d{4})-Q([1-4])$/))) return +m[1]! * 100 + (+m[2]! - 1) * 3 + 1
    if ((m = p.match(/^(\d{4})$/))) return +m[1]! * 100
    return Number.MAX_SAFE_INTEGER
  }
  return key(a) - key(b) || a.localeCompare(b)
}
